import type {
  GbpKpi,
  GbpNextPost,
  GbpOfferSync,
  GbpPhotoFreshness,
  GbpPost,
  GbpProfileItem,
  GbpQa,
  GbpReview,
  GbpRating,
  GbpReviewSnapshot,
  GbpTone,
} from '@elva/ui';
import { growthGet, growthGetWithMeta } from '@/data/http';
import { deepCamel, num, relativeTimeFromIso, stableNumericId, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

interface BeGbpHealthComponent {
  score?: number;
  weight?: number;
  weighted?: number;
}

interface BeGbpOverview {
  clinic_id?: number;
  external_id?: string;
  profile?: {
    business_name?: string;
    category_primary?: string;
    review_count?: number;
    rating?: number;
    last_synced_at?: string;
  };
  health_score?: {
    score?: number;
    components?: Record<string, BeGbpHealthComponent | number>;
    computed_at?: string;
  };
  photos_summary?: {
    total_count?: number;
    by_category?: Record<string, number>;
    stale_count?: number;
    missing_essential_prompts?: string[];
  };
  posts_summary?: {
    last_30d_count?: number;
    last_post_at?: string;
  };
  reviews_summary?: {
    total_count?: number;
    avg_rating?: number;
    last_30d_count?: number;
    response_rate_30d_pct?: number;
    drafted_responses_pending?: number;
  };
  qa_summary?: {
    open_questions_count?: number;
    answered_pct?: number;
  };
  field_checklist?: Array<{
    field?: string;
    label?: string;
    complete?: boolean;
    priority?: string;
  }>;
  offer_sync_status?: Record<string, unknown>;
  avg_response_time_hours?: number;
  competitor_photo_gap?: number;
}

interface BeGbpReview {
  id: string | number;
  reviewer?: string;
  rating?: string | number;
  text?: string;
  date?: string;
  created_at?: string;
  draft?: string;
  draft_response?: string;
  status?: string;
  response_status?: string;
}

interface BeGbpPost {
  id: string | number;
  post_type?: string;
  type?: string;
  title?: string;
  body?: string;
  status?: string;
  date?: string;
  created_at?: string;
  event_start_at?: string;
  event_end_at?: string;
  tone?: string;
  content_tag?: string;
}

interface BeGbpPostsPayload {
  items?: BeGbpPost[];
  next_scheduled?: {
    title?: string;
    body?: string;
    scheduled_for?: string;
  };
}

interface BeGbpQa {
  id: string | number;
  q?: string;
  question?: string;
  a?: string;
  answer?: string;
  status?: string;
  category?: string;
}

export interface GbpData {
  kpis: GbpKpi[];
  reviews: GbpReview[];
  posts: GbpPost[];
  qas: GbpQa[];
  reviewIdMap: Record<number, string>;
  postIdMap: Record<number, string>;
  qaIdMap: Record<number, string>;
  profileItems?: GbpProfileItem[];
  profileCompletenessPct?: number;
  photoFreshness?: GbpPhotoFreshness;
  reviewSnapshot?: GbpReviewSnapshot;
  businessName?: string;
  offerSync?: GbpOfferSync;
  nextPost?: GbpNextPost;
  reviewsDegraded?: boolean;
}

function adaptKpi(row: Record<string, unknown>): GbpKpi {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    key: str(c.key),
    label: str(c.label),
    value: str(c.value),
    sub: str(c.sub, ''),
    tone: (enumLower(c.tone as string, 'neutral') as GbpTone) ?? 'neutral',
  };
}

function componentScore(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'object') return num((value as BeGbpHealthComponent).score);
  return num(value);
}

function mapProfilePriority(value: unknown): import('@elva/ui').GbpPriority {
  const priority = str(value, 'medium').toLowerCase();
  if (priority === 'critical') return 'Critical';
  if (priority === 'high') return 'High';
  if (priority === 'low') return 'Low';
  return 'Medium';
}

function formatPostType(postType: string): string {
  return postType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatPostDate(row: BeGbpPost): string {
  if (row.date) return str(row.date);
  if (row.event_start_at && row.event_end_at) {
    const start = relativeTimeFromIso(row.event_start_at);
    const end = relativeTimeFromIso(row.event_end_at);
    return `${start} – ${end}`;
  }
  if (row.created_at) return relativeTimeFromIso(row.created_at);
  return '—';
}

function adaptReviewStatus(responseStatus: unknown): GbpReview['status'] {
  const status = str(responseStatus, 'pending').toLowerCase();
  if (status === 'approved' || status === 'live') return 'approved';
  return 'pending';
}

function adaptPostStatus(status: unknown): GbpPost['status'] {
  const wire = str(status, 'live').toLowerCase();
  if (wire === 'live') return 'Live';
  if (wire === 'scheduled' || wire === 'drafted' || wire === 'approved') return 'Scheduled';
  return 'Published';
}

function postTone(postType: string): GbpTone {
  const type = postType.toLowerCase();
  if (type === 'offer') return 'success';
  if (type === 'event') return 'gold';
  if (type === 'article' || type === 'update') return 'teal';
  return 'neutral';
}

function adaptQaStatus(status: unknown): GbpQa['status'] {
  const wire = str(status, 'pending').toLowerCase();
  if (wire === 'live' || wire === 'archived') return 'Posted';
  return 'Pending';
}

function adaptOfferSync(raw: Record<string, unknown> | undefined): GbpOfferSync | undefined {
  if (!raw) return undefined;
  const c = deepCamel<Record<string, unknown>>(raw);
  const status = str(c.status, '');
  if (!status) return undefined;
  return {
    status: status.charAt(0).toUpperCase() + status.slice(1),
    offer: str(c.offer ?? c.title, '—'),
    body: str(c.body ?? c.message, ''),
    footnote: str(c.footnote ?? c.note, ''),
  };
}

function adaptFieldChecklist(
  items: BeGbpOverview['field_checklist'],
): GbpProfileItem[] {
  return (items ?? []).map((row) => ({
    field: str(row.field ?? row.label),
    complete: Boolean(row.complete),
    priority: mapProfilePriority(row.priority),
  }));
}

const PHOTO_PROMPT_LABELS: Record<string, string> = {
  'ext-sign': 'Exterior signage',
  'ext-building': 'Building exterior',
  'ext-entrance': 'Entrance',
  'int-reception': 'Reception area',
  'int-waiting': 'Waiting room',
  'int-operatory': 'Operatory',
  'team-group': 'Team group photo',
  'team-doctor': 'Doctor photo',
};

function photoPromptPriority(promptId: string): import('@elva/ui').GbpPriority {
  if (promptId.startsWith('ext-')) return 'Critical';
  if (promptId.startsWith('int-')) return 'High';
  if (promptId.startsWith('team-')) return 'Medium';
  return 'Medium';
}

function formatPhotoPromptLabel(promptId: string): string {
  return PHOTO_PROMPT_LABELS[promptId] ?? promptId.replace(/-/g, ' ');
}

function daysSinceIso(iso: string | undefined): number {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 0;
  return Math.max(0, Math.round((Date.now() - then) / 86_400_000));
}

function adaptProfileItems(missingPrompts: string[] | undefined): GbpProfileItem[] {
  return (missingPrompts ?? []).map((promptId) => ({
    field: formatPhotoPromptLabel(promptId),
    complete: false,
    priority: photoPromptPriority(promptId),
  }));
}

function adaptGbpKpis(be: BeGbpOverview): GbpKpi[] {
  const reviews = be.reviews_summary;
  const posts = be.posts_summary;
  const qa = be.qa_summary;
  const completeness = componentScore(be.health_score?.components?.profile_completeness);

  const pending = num(reviews?.drafted_responses_pending);
  const responseRate = num(reviews?.response_rate_30d_pct);
  const last30Reviews = num(reviews?.last_30d_count);
  const posts30d = num(posts?.last_30d_count);
  const openQa = num(qa?.open_questions_count);
  const answeredPct = num(qa?.answered_pct, 100);

  return [
    {
      key: 'pending',
      label: 'Pending Review Responses',
      value: String(pending),
      sub:
        pending > 0
          ? 'ELVA drafted — awaiting your approval'
          : 'No drafts waiting for approval',
      tone: pending > 0 ? 'terra' : 'neutral',
    },
    {
      key: 'response-rate',
      label: 'Response Rate',
      value: `${Math.round(responseRate)}%`,
      sub: `${last30Reviews} review${last30Reviews === 1 ? '' : 's'} in the last 30 days`,
      tone: responseRate >= 80 ? 'teal' : responseRate >= 50 ? 'gold' : 'terra',
    },
    {
      key: 'posts',
      label: 'GBP Posts This Month',
      value: String(posts30d),
      sub: 'Target: 4/month (1 per week)',
      tone: posts30d >= 4 ? 'success' : posts30d >= 2 ? 'neutral' : 'gold',
    },
    {
      key: 'qa',
      label: 'Q&As Answered',
      value: openQa > 0 ? `${openQa} open` : `${Math.round(answeredPct)}%`,
      sub: openQa > 0 ? `${openQa} pending your approval` : `${Math.round(answeredPct)}% answered`,
      tone: openQa > 0 ? 'gold' : 'neutral',
    },
    {
      key: 'completeness',
      label: 'Profile Completeness',
      value: `${Math.round(completeness)}%`,
      sub:
        completeness >= 80
          ? 'Profile in good shape'
          : 'Complete missing fields to rank higher in Map Pack',
      tone: completeness >= 80 ? 'success' : completeness >= 60 ? 'gold' : 'terra',
    },
  ];
}

function adaptPhotoFreshness(be: BeGbpOverview): GbpPhotoFreshness {
  const photos = be.photos_summary;
  const photoScore = componentScore(be.health_score?.components?.photo_freshness);
  const totalPhotos = num(photos?.total_count);
  const staleCount = num(photos?.stale_count);
  const missingCount = photos?.missing_essential_prompts?.length ?? 0;
  const daysSinceUpload = daysSinceIso(be.posts_summary?.last_post_at ?? be.profile?.last_synced_at);

  let body = 'Upload recent photos to improve Map Pack visibility.';
  if (missingCount > 0) {
    body = `${missingCount} essential photo${missingCount === 1 ? '' : 's'} still missing from your profile.`;
  } else if (staleCount > 0) {
    body = `${staleCount} photo${staleCount === 1 ? '' : 's'} may be stale — refresh to stay competitive.`;
  } else if (totalPhotos === 0) {
    body = 'No GBP photos yet. Upload team, interior, and exterior shots to boost discovery.';
  }

  return {
    daysSinceUpload,
    progressPct: Math.round(photoScore),
    body,
    totalPhotos,
    competitorAvg: num(be.competitor_photo_gap),
  };
}

function adaptReviewSnapshot(be: BeGbpOverview): GbpReviewSnapshot {
  const profile = be.profile;
  const reviews = be.reviews_summary;
  const rating = profile?.rating ?? reviews?.avg_rating;
  const total = reviews?.total_count ?? profile?.review_count ?? 0;
  const last30 = num(reviews?.last_30d_count);
  const responseRate = Math.round(num(reviews?.response_rate_30d_pct));

  const avgResponseHours = be.avg_response_time_hours;

  return {
    stats: [
      {
        key: 'rating',
        value: rating != null ? String(rating) : '—',
        label: 'Your Rating',
        tone: 'teal',
      },
      {
        key: 'total',
        value: String(total),
        label: 'Total Reviews',
        tone: 'neutral',
      },
      {
        key: 'last30',
        value: last30 > 0 ? `+${last30}` : '0',
        label: 'Last 30 Days',
        tone: last30 > 0 ? 'success' : 'neutral',
      },
    ],
    body:
      avgResponseHours != null
        ? `${responseRate}% response rate in the last 30 days. Avg response time: ${avgResponseHours}h.`
        : `${responseRate}% response rate in the last 30 days.`,
  };
}

function adaptGbpOverview(be: BeGbpOverview) {
  const profileCompletenessPct = Math.round(
    componentScore(be.health_score?.components?.profile_completeness),
  );
  const checklistItems = adaptFieldChecklist(be.field_checklist);
  const profileItems =
    checklistItems.length > 0
      ? checklistItems
      : adaptProfileItems(be.photos_summary?.missing_essential_prompts);

  const offerSync = adaptOfferSync(be.offer_sync_status);

  return {
    kpis: adaptGbpKpis(be),
    profileItems,
    profileCompletenessPct,
    photoFreshness: adaptPhotoFreshness(be),
    reviewSnapshot: adaptReviewSnapshot(be),
    ...(offerSync ? { offerSync } : {}),
    ...(be.profile?.business_name ? { businessName: be.profile.business_name } : {}),
  };
}

function adaptReview(row: BeGbpReview): { review: GbpReview; backendId: string } {
  const backendId = String(row.id);
  const ratingNum = typeof row.rating === 'number' ? row.rating : Number(row.rating);
  const ratingMap: Record<number, GbpRating> = {
    1: 'ONE',
    2: 'TWO',
    3: 'THREE',
    4: 'FOUR',
    5: 'FIVE',
  };

  return {
    backendId,
    review: {
      id: stableNumericId(row.id),
      reviewer: str(row.reviewer, 'Anonymous'),
      rating: ratingMap[ratingNum] ?? 'FIVE',
      text: str(row.text, ''),
      date: row.created_at
        ? relativeTimeFromIso(row.created_at)
        : str(row.date, '—'),
      draft: str(row.draft ?? row.draft_response, ''),
      status: adaptReviewStatus(row.response_status ?? row.status),
    },
  };
}

function adaptPost(row: BeGbpPost): GbpPost {
  const postType = str(row.post_type ?? row.type, 'update');
  return {
    id: stableNumericId(row.id),
    type: formatPostType(postType),
    title: str(row.title, str(row.body, 'Untitled post').slice(0, 80)),
    status: adaptPostStatus(row.status),
    date: formatPostDate(row),
    tone: postTone(postType),
  };
}

function adaptQa(row: BeGbpQa): { qa: GbpQa; backendId: string } {
  const backendId = String(row.id);
  return {
    backendId,
    qa: {
      id: stableNumericId(row.id),
      q: str(row.q ?? row.question),
      a: str(row.a ?? row.answer, ''),
      status: adaptQaStatus(row.status),
      ...(row.category ? { category: str(row.category) } : {}),
    },
  };
}

export async function fetchGbpOverview(): Promise<
  Pick<GbpData, 'kpis'> &
    Partial<
      Pick<
        GbpData,
        | 'profileItems'
        | 'profileCompletenessPct'
        | 'photoFreshness'
        | 'reviewSnapshot'
        | 'businessName'
        | 'offerSync'
      >
    >
> {
  const be = await growthGet<BeGbpOverview>('/gbp/').catch(() => null);
  if (!be) return { kpis: [] };

  if (be.profile || be.health_score || be.reviews_summary) {
    return adaptGbpOverview(be);
  }

  const legacyKpis = (be as { kpis?: Array<Record<string, unknown>> }).kpis?.map(adaptKpi) ?? [];
  return { kpis: legacyKpis };
}

export async function fetchGbpReviews(page = 1): Promise<{
  reviews: GbpReview[];
  reviewIdMap: Record<number, string>;
  reviewsDegraded?: boolean;
}> {
  const { data, meta } = await growthGetWithMeta<BeGbpReview[] | { items?: BeGbpReview[] }>(
    '/gbp/reviews/',
    { page, limit: 50 },
  );
  const rows = Array.isArray(data) ? data : data.items ?? [];
  const reviews: GbpReview[] = [];
  const reviewIdMap: Record<number, string> = {};
  const usedUiIds = new Set<number>();
  for (const [index, row] of rows.entries()) {
    const { review, backendId } = adaptReview(row);
    let uiId = review.id;
    if (usedUiIds.has(uiId)) {
      uiId = stableNumericId(`${backendId}-${index}`);
      while (usedUiIds.has(uiId)) uiId += 1;
    }
    usedUiIds.add(uiId);
    const uniqueReview = uiId === review.id ? review : { ...review, id: uiId };
    reviews.push(uniqueReview);
    reviewIdMap[uiId] = backendId;
  }
  const freshness = (meta as { data_freshness?: string } | undefined)?.data_freshness;
  return {
    reviews,
    reviewIdMap,
    ...(freshness === 'degraded' ? { reviewsDegraded: true } : {}),
  };
}

export async function fetchGbpPosts(page = 1): Promise<{
  posts: GbpPost[];
  postIdMap: Record<number, string>;
  nextPost?: GbpNextPost;
}> {
  const data = await growthGet<BeGbpPost[] | BeGbpPostsPayload>('/gbp/posts/', { page, limit: 50 });
  const payload: BeGbpPostsPayload = Array.isArray(data) ? { items: data } : data;
  const rows = payload.items ?? [];
  const posts: GbpPost[] = [];
  const postIdMap: Record<number, string> = {};
  const usedUiIds = new Set<number>();
  for (const [index, row] of rows.entries()) {
    const post = adaptPost(row);
    const backendId = String(row.id);
    let uiId = post.id;
    if (usedUiIds.has(uiId)) {
      uiId = stableNumericId(`${backendId}-${index}`);
      while (usedUiIds.has(uiId)) uiId += 1;
    }
    usedUiIds.add(uiId);
    posts.push(uiId === post.id ? post : { ...post, id: uiId });
    postIdMap[uiId] = backendId;
  }

  const scheduled = payload.next_scheduled;
  const nextPost =
    scheduled?.body || scheduled?.title
      ? {
          title: str(scheduled.title, 'Next scheduled post'),
          body: str(scheduled.body),
          footnote: scheduled.scheduled_for
            ? `Scheduled ${relativeTimeFromIso(scheduled.scheduled_for)}`
            : 'Auto-publishing is enabled for GBP posts when approved.',
        }
      : undefined;

  return {
    posts,
    postIdMap,
    ...(nextPost ? { nextPost } : {}),
  };
}

export async function fetchGbpQa(page = 1): Promise<{ qas: GbpQa[]; qaIdMap: Record<number, string> }> {
  const data = await growthGet<BeGbpQa[] | { items?: BeGbpQa[] }>('/gbp/qa/', { page, limit: 50 });
  const rows = Array.isArray(data) ? data : data.items ?? [];
  const qas: GbpQa[] = [];
  const qaIdMap: Record<number, string> = {};
  for (const row of rows) {
    const { qa, backendId } = adaptQa(row);
    qas.push(qa);
    qaIdMap[qa.id] = backendId;
  }
  return { qas, qaIdMap };
}

export async function fetchGbp(page = 1): Promise<GbpData> {
  const [overview, reviewsData, postsData, qaData] = await Promise.all([
    fetchGbpOverview(),
    fetchGbpReviews(page),
    fetchGbpPosts(page),
    fetchGbpQa(page),
  ]);

  return {
    kpis: overview.kpis,
    reviews: reviewsData.reviews,
    posts: postsData.posts,
    qas: qaData.qas,
    reviewIdMap: reviewsData.reviewIdMap,
    postIdMap: postsData.postIdMap,
    qaIdMap: qaData.qaIdMap,
    ...(reviewsData.reviewsDegraded ? { reviewsDegraded: true } : {}),
    ...(postsData.nextPost ? { nextPost: postsData.nextPost } : {}),
    ...(overview.offerSync ? { offerSync: overview.offerSync } : {}),
    ...(overview.businessName
      ? {
          profileItems: overview.profileItems,
          profileCompletenessPct: overview.profileCompletenessPct,
          photoFreshness: overview.photoFreshness,
          reviewSnapshot: overview.reviewSnapshot,
          businessName: overview.businessName,
        }
      : {}),
  };
}

export async function approveGbpReview(id: string) {
  const { growthPost } = await import('@/data/http');
  return growthPost(`/gbp/reviews/${id}/approve-response/`);
}

export async function approveGbpQa(id: string) {
  const { growthPost } = await import('@/data/http');
  return growthPost(`/gbp/qa/${id}/approve-response/`);
}

export async function draftGbpReviewResponse(id: string, body?: { responseTone?: string }) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync(
    `/gbp/reviews/${id}/draft-response/`,
    body?.responseTone ? { response_tone: body.responseTone } : {},
  );
}

export async function editGbpReviewResponse(id: string, responseText: string) {
  const { growthPatch } = await import('@/data/http');
  return growthPatch(`/gbp/reviews/${id}/response/`, { response_text: responseText });
}

export async function createGbpPost(body: {
  postType?: string;
  contentTag?: string;
  title?: string;
  body?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync('/gbp/posts/', {
    post_type: body.postType,
    content_tag: body.contentTag,
    title: body.title,
    body: body.body,
    cta_label: body.ctaLabel,
    cta_url: body.ctaUrl,
  });
}

export async function approveGbpPost(id: string) {
  const { growthPost } = await import('@/data/http');
  return growthPost(`/gbp/posts/${id}/approve/`);
}

export async function draftGbpQaResponse(id: string) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync(`/gbp/qa/${id}/draft-response/`);
}
